import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getCurrentUser, changePassword } from '../services/api';
import AppShell from '../components/layout/AppShell';
import './Profile.css';

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setRestaurants } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [activeTab, setActiveTab] = useState('profile');
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');
  const [passwordSuccessOpen, setPasswordSuccessOpen] = useState(false);

  const handleProfileChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordChange = (e) => {
    setPasswordData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    // Note: Update profile endpoint would need to be implemented
    setSuccessMsg(t('profile.updateSuccess'));
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('profile.password.mismatch'));
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError(t('profile.password.tooShort'));
      return;
    }

    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSuccessOpen(true);
    } catch (err) {
      setError(err?.response?.data?.error || t('profile.password.changeError'));
    }
  };

  const handleSuccessDialogConfirm = () => {
    setPasswordSuccessOpen(false);
    navigate('/');
  };

  return (
    <AppShell>
    <div className="profile-page fade-in">
      <div className="profile-container">
        <div className="profile-header">
          <h1>{t('profile.title')}</h1>
          <p>{t('profile.subtitle')}</p>
        </div>

        <div className="profile-tabs">
          <button
            className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {t('profile.tabs.profile')}
          </button>
          <button
            className={`tab-button ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            {t('profile.tabs.password')}
          </button>
        </div>

        {successMsg && <div className="success-message">{successMsg}</div>}
        {error && <div className="error-message">{error}</div>}

        {activeTab === 'profile' && (
          <div className="profile-content">
            <form onSubmit={handleUpdateProfile}>
              <div className="form-group">
                <label>{t('profile.fields.name')}</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleProfileChange}
                />
              </div>

              <div className="form-group">
                <label>{t('profile.fields.email')}</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleProfileChange}
                  disabled
                />
                <small style={{ color: 'var(--text-muted)' }}>{t('profile.fields.emailHint')}</small>
              </div>

              <div className="form-group">
                <label>{t('profile.fields.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleProfileChange}
                />
              </div>

              <div className="form-group">
                <label>{t('profile.fields.role')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={user?.role}
                  disabled
                />
              </div>

              <button type="submit" className="btn btn-primary">
                {t('profile.updateButton')}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'password' && (
          <div className="profile-content">
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>{t('profile.password.currentPassword')}</label>
                <input
                  type="password"
                  name="currentPassword"
                  className="form-input"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('profile.password.newPassword')}</label>
                <input
                  type="password"
                  name="newPassword"
                  className="form-input"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label>{t('profile.password.confirmPassword')}</label>
                <input
                  type="password"
                  name="confirmPassword"
                  className="form-input"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={8}
                />
              </div>

              <button type="submit" className="btn btn-primary">
                {t('profile.password.changeButton')}
              </button>
            </form>
          </div>
        )}
      </div>

      <Dialog
        open={passwordSuccessOpen}
        onClose={handleSuccessDialogConfirm}
        aria-labelledby="password-success-title"
      >
        <DialogTitle id="password-success-title">
          {t('profile.password.changeSuccessTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('profile.password.changeSuccess')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogConfirm} variant="contained" autoFocus>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
    </AppShell>
  );
};

export default Profile;
